import { useEffect, useState } from 'react';

// Palette chosen for good contrast with white initials text.
const AVATAR_COLORS = [
    '#F43F5E', '#F97316', '#F59E0B', '#84CC16', '#10B981', '#14B8A6',
    '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', '#D946EF', '#EC4899',
];

function hashStringToIndex(value: string, mod: number) {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
    }
    return hash % mod;
}

// First letter of every space-separated word (e.g. "New York Tennis Club" -> "NYTC", "Jane Doe" -> "JD"),
// used the same way for people and organizations.
function getInitials(name?: string) {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return parts.slice(0, 4).map(p => p[0]).join('').toUpperCase();
}

interface AvatarProps {
    src?: string | null;
    name?: string;
    seed?: string;
    size?: number;
    className?: string;
}

// Falls back to initials on a color derived from `seed` (typically a user or organization id) when there's no image,
// so the same identity always gets the same color and different ones are visually distinguishable.
export default function Avatar({ src, name, seed, size = 40, className = '' }: AvatarProps) {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [src]);

    if (src && !failed) {
        return (
            <img
                src={src}
                alt={name || 'Profile'}
                className={`rounded-full object-cover flex-shrink-0 ${className}`}
                style={{ width: size, height: size }}
                onError={() => setFailed(true)}
            />
        );
    }

    const colorSeed = seed || name || 'user';
    const backgroundColor = AVATAR_COLORS[hashStringToIndex(colorSeed, AVATAR_COLORS.length)];
    const initials = getInitials(name);

    return (
        <div
            className={`rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${className}`}
            style={{ width: size, height: size, backgroundColor, fontSize: Math.round(size * (initials.length > 2 ? 0.32 : 0.4)) }}
        >
            {initials}
        </div>
    );
}
